/**
 * Flight-logbook lookup: the nearest landing site within 1,000 m, then city within 25 km.
 * Uses the landing-sites pack's demo_index, which excludes archived,
 * closed and waypoint sites. Airport/heliport navdata is not a logbook source.
 */

export const SITE_RADIUS_M = 1000;
export const CITY_RADIUS_M = 25_000;

const EARTH_RADIUS_M = 6_371_008.8;
const METRES_PER_DEGREE = 111_320;

export interface NearestPlace {
  kind: "landingSite" | "city";
  id: number;
  name: string;
  /** ICAO/site identifier (`index2`), "" for a site without one. */
  code: string;
  latitude: number;
  longitude: number;
  distanceM: number;
}

export type NamedQuery = (name: string, params: Record<string, unknown>) => Promise<Record<string, unknown>[]>;

export function distanceM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const rad = Math.PI / 180;
  const a =
    Math.sin(((lat2 - lat1) * rad) / 2) ** 2 +
    Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(((lon2 - lon1) * rad) / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** Query params for a box of `radiusM` around the position. */
// ponytail: a box across the antimeridian is not split, so a position within the radius of ±180° misses the far side.
export function searchBox(lat: number, lon: number, radiusM: number): Record<string, number> {
  const cosLat = Math.max(Math.cos((lat * Math.PI) / 180), 0.01);
  const dLat = radiusM / METRES_PER_DEGREE;
  const dLon = dLat / cosLat;
  return {
    lat,
    lon,
    lonScale: cosLat * cosLat,
    minX: lon - dLon,
    maxX: lon + dLon,
    minY: lat - dLat,
    maxY: lat + dLat,
  };
}

function nearestRow(
  rows: Record<string, unknown>[],
  lat: number,
  lon: number,
  radiusM: number,
  kind: NearestPlace["kind"]
): NearestPlace | null {
  let best: NearestPlace | null = null;
  for (const r of rows) {
    const latitude = Number(r["latitude"]);
    const longitude = Number(r["longitude"]);
    if (r["latitude"] == null || r["longitude"] == null || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      continue;
    }
    const d = distanceM(lat, lon, latitude, longitude);
    if (d > radiusM || (best && d >= best.distanceM)) continue;
    best = {
      kind,
      id: Number(r["id"]),
      name: String(r["name"] ?? ""),
      code: String(r["code"] ?? ""),
      latitude,
      longitude,
      distanceM: d,
    };
  }
  return best;
}

/** Display name for the logbook: `CODE, Name` for a site with a code, otherwise the name. */
export function placeName(place: NearestPlace): string {
  return place.code ? `${place.code}, ${place.name}` : place.name;
}

/**
 * The nearest landing site within 1,000 m, else the nearest city within 25 km, else null.
 * A failed site read throws rather than silently substituting a city.
 */
export async function findNearestPlace(lat: number, lon: number, query: NamedQuery): Promise<NearestPlace | null> {
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;

  const sites = await query("nearestLandingSites", searchBox(lat, lon, SITE_RADIUS_M));
  const site = nearestRow(sites, lat, lon, SITE_RADIUS_M, "landingSite");
  if (site) return site;
  const cities = await query("nearestCities", searchBox(lat, lon, CITY_RADIUS_M));
  return nearestRow(cities, lat, lon, CITY_RADIUS_M, "city");
}
